import type { TranslationContent, Langs } from '@CS/components/TranslationProvider/types';
import type { SetValueConfig, UseFormRegisterReturn } from 'react-hook-form';
import Input from '@CS/components/Form/InputField/InputField.module.css';
import type { SelectProps } from '@CS/components/Form/Fields/types';
import { Context } from '@CS/components/TranslationProvider';
import { Content, InputField } from '@CS/components/Form';
import { type ChangeEvent, useState } from 'react';
import { useTranslation } from '@CS/hooks';
import CSS from './SelectField.module.css';
import { useContext } from 'react';

const Translations = Content as TranslationContent;

type SelectFieldProps = {
  setValue: (name: string, value: unknown, config?: SetValueConfig) => unknown;
  placeholder?: Record<Langs, string>;
  register: UseFormRegisterReturn;
  options: SelectProps['options'];

  onBlur?: () => void;
  disabled?: boolean;
  search?: boolean;

  error?: boolean;
  width?: number;
  last?: boolean;
  name: string;
};

export const SelectField = ({
  placeholder = { en: '', ar: '' },
  disabled = false,
  search = false,
  error = false,
  last = false,
  width = 100,
  setValue,
  register,
  options,
  onBlur,
  name
}: SelectFieldProps) => {
  const [dropdown, setDropdown] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filtered, setFiltered] = useState(options);

  const fieldLabel = useTranslation(Translations[name]);
  const localizedPlaceholder = useTranslation(placeholder);
  const fieldError = useTranslation(Translations[`${name}Error`]);
  const lang = useContext(Context).toLocaleUpperCase() as Uppercase<Langs>;

  const onClick = (option: SelectProps['options'][number]) => {
    setSearchText(option[`name${lang}`]);
    setValue(name, option.value);
    setDropdown(false);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    register.onChange(event);

    setValue(name, value);
    setSearchText(value);
    setDropdown(true);

    setFiltered(
      options.filter((option) =>
        option[`name${lang}`].toLowerCase().includes(value.toLowerCase())
      )
    );
  };

  const onFocusOut = () => ~setDropdown(false) && onBlur?.();

  return (
    <div
      className={Input.fieldContainer}
      style={{
        marginBottom: (last && '0px') || 'revert-layer',
        position: 'relative',
        width: `${width}%`
      }}
    >
      {search ? (
        <>
          <InputField
            placeholder={placeholder}
            onChange={onChange}
            disabled={disabled}
            register={register}
            onBlur={onFocusOut}
            value={searchText}
            width={width}
            name={name}
            last
          />

          {dropdown && (
            <menu className={CSS.selectDropdown}>
              {filtered.map(option => (
                <option onClick={onClick.bind(null, option)} key={option.value}>
                  {option[`name${lang}`]}
                </option>
              ))}
            </menu>
          )}
        </>
      ) : (
        <>
          <label htmlFor={name} className={Input.fieldLabel}>
            {fieldLabel}
          </label>

          <select
            id={name}
            {...register}
            onBlur={onBlur}
            disabled={disabled}
            className={Input.selectField}
            style={{ width: `${width}%` }}
          >
            <option className={CSS.selectOption} defaultValue="" value="" disabled>
              {localizedPlaceholder}
            </option>

            {options.map((option) => (
              <option key={option.value} value={option.value} className={CSS.selectOption}>
                {option.emoji + ' ' + option[`name${lang}`]}
              </option>
            ))}
          </select>

          <span className={CSS.selectArrow}>&#8964;</span>
        </>
      )}

      {error && <em className={Input.fieldError}>{fieldError}</em>}
    </div>
  );
};
